import React, { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import GlobalTimelineTable from './table'
import DateGroupPicker from '../../../components/UI/dateGroupPicker'
import TodayTag from '../../../components/UI/todayTag'
import PageLoading from '../../../components/UI/status/loading'
import { getThisWeekRange } from '../../../utils/dateHandles'
import { getGlobalTimeline } from '../../../api/timeline'
import { useHistory } from 'react-router-dom'
import './index.sass'

const Container = styled.div`
  .globalTimeline {
    padding: 24px;
  }
`

const dateRanges = [
  {
    name: 'Week',
    barWidth: 20,
    picker: 'week',
    formatX: 'ddd',
    formatTooltip: 'ddd D'
  },
  {
    name: 'Month',
    barWidth: 8,
    picker: 'month',
    formatX: 'MMM/D'
  },
  {
    name: 'Year',
    barWidth: 20,
    type: 'annual',
    picker: 'year',
    formatX: 'MMM'
  },
  {
    name: 'Custom',
    picker: 'date',
    type: 'custom'
  }
]

export default function GlobalTimeline(props) {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [list, setList] = useState([])
  const [filters, setFilters] = useState({})
  const [state, setState] = useState({
    from: getThisWeekRange().startDate.format('YYYY-MM-DD'),
    to: getThisWeekRange().endDate.format('YYYY-MM-DD')
  })

  const { from, to } = state

  useEffect(() => {
    const initial = async () => {
      const { startDate, endDate } = getThisWeekRange()
      fetchData(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'))
    }
    initial()
  }, [])

  const fetchData = useCallback(async (fromDate, toDate, extraFilters = {}) => {
    setLoading(true)
    try {
      const params = {
        start_date: fromDate,
        end_date: toDate,
        ...extraFilters
      }

      if (extraFilters.event_type && extraFilters.event_type.length > 0) {
        params.event_type = extraFilters.event_type.join(',')
      }

      const result = await getGlobalTimeline(params)
      setList(result.data || result.timeline || [])
    } catch (error) {
      console.error('Error fetching Timeline Overview:', error)
      message.error('Failed to fetch timeline data')
      setList([])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleDateFilter = useCallback(
    (fromDate, toDate) => {
      setState({ from: fromDate, to: toDate })
      setLoading(true)
      fetchData(fromDate, toDate, filters)
    },
    [fetchData, filters],
  )

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setFilters(newFilters)
      setLoading(true)
      fetchData(from, to, newFilters)
    },
    [fetchData, from, to],
  )

  const handleRowClick = useCallback(
    (record) => {
      if (record.client_id) {
        history.push(`/client/${record.client_id}/detail/timeline`)
      }
    },
    [history],
  )

  // Calculate statistics
  const stats = useCallback(() => {
    const renewalCount = list.filter(
      (item) =>
        item.event_type &&
        (item.event_type === 'renewal' || item.event_type === 'renew'),
    ).length

    const qmeCount = list.filter(
      (item) =>
        item.event_type &&
        (item.event_type === 'qme1' ||
          item.event_type === 'qme2' ||
          item.event_type === 'qme3'),
    ).length

    const physicalAssessmentCount = list.filter(
      (item) => item.event_type && item.event_type === 'physical_assessment',
    ).length

    const programStartCount = list.filter(
      (item) => item.event_type && item.event_type === 'program_start',
    ).length

    const initialContactCount = list.filter(
      (item) => item.event_type && item.event_type === 'initial_contact',
    ).length

    const initialConsultationCount = list.filter(
      (item) => item.event_type && item.event_type === 'consultation',
    ).length

    const otherCount =
      list.length -
      renewalCount -
      qmeCount -
      physicalAssessmentCount -
      programStartCount -
      initialContactCount -
      initialConsultationCount

    return {
      total: list.length,
      renewals: renewalCount,
      qme: qmeCount,
      evaluations: physicalAssessmentCount,
      programStart: programStartCount,
      initialContact: initialContactCount,
      initialConsultation: initialConsultationCount,
      other: otherCount
    }
  }, [list])

  const statsData = stats()

  return (
    <Container>
      <div className="globalTimeline">
        <div className="page-title">Timeline Overview</div>

        <div className="top-opr">
          <DateGroupPicker
            dateRanges={dateRanges}
            fetchData={handleDateFilter}
            defaultSelectedDate={0}
            from={from}
            to={to}
          />
          <TodayTag />
        </div>

        <div className="stats-panel">
          <div className="stat-card">
            <div className="stat-label">Total Events</div>
            <div className="stat-value">{statsData.total}</div>
          </div>
          <div className="stat-card renewals">
            <div className="stat-label">Renewals</div>
            <div className="stat-value">{statsData.renewals}</div>
          </div>
          <div className="stat-card qme">
            <div className="stat-label">QMEs</div>
            <div className="stat-value">{statsData.qme}</div>
          </div>
          <div className="stat-card evaluations">
            <div className="stat-label">Evaluations</div>
            <div className="stat-value">{statsData.evaluations}</div>
          </div>
          <div className="stat-card program-start">
            <div className="stat-label">Program Starts</div>
            <div className="stat-value">{statsData.programStart}</div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#13c2c2' }}>
            <div className="stat-label">Initial Contact</div>
            <div className="stat-value">{statsData.initialContact}</div>
          </div>
          <div className="stat-card" style={{ borderLeftColor: '#8c8c8c' }}>
            <div className="stat-label">Initial Consultation</div>
            <div className="stat-value">{statsData.initialConsultation}</div>
          </div>
          <div
            className="stat-card other"
            style={{ borderLeftColor: '#a5a3a3ff' }}
          >
            <div className="stat-label">Others</div>
            <div className="stat-value">{statsData.other}</div>
          </div>
        </div>

        {loading && (
          <>
            <div className="ant-modal-mask" />
            <div
              className="ant-modal-wrap"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around'
              }}
            >
              <PageLoading />
            </div>
          </>
        )}

        <GlobalTimelineTable
          dataSource={list}
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          onFiltersChange={handleFiltersChange}
          onRowClick={handleRowClick}
        />

        {list.length === 0 && !loading && (
          <div
            style={{ textAlign: 'center', padding: '48px', color: '#8c8c8c' }}
          >
            No timeline events found for the selected date range.
          </div>
        )}
      </div>
    </Container>
  )
}
